/**
 * SearchResults.js - Enhanced component with horizontal suggestions above results
 *
 * Changes:
 * - Added horizontal suggestions list above results
 * - Suggestions separated by line from visual results
 * - Fixed suggestion clicks to update visual results
 * - Better integration with real-time search
 */
import React, { memo, useEffect, useState, useCallback, useRef } from 'react';
import ResultCard from './ResultCard';
import ThumbnailHoverModal from '../ThumbnailHoverModal';
import PDPModal from '../PDPModal';
import { useAskArloPanel } from '../../contexts/AskArloContext';

/**
 * Enhanced SearchResults component with horizontal suggestions
 * @returns {JSX.Element} SearchResults component
 */
const SearchResults = ({ searchState }) => {
  const {
    query,
    results,
    textSuggestions,
    loading,
    suggestionsLoading,
    error,
    totalResults,
    page,
    changePage,
    totalPages,
    isFirstPage,
    isLastPage,
    hasResults,
    isRealTimeSearch,
    selectSuggestion,
    searchHistory,
    clearSearchHistory,
    connectionError,
    retryCount,
    retrySearch,
  } = searchState;

  const { enabled: askArloEnabled, openPanel: openAskArlo } = useAskArloPanel();

  const [showResults, setShowResults] = useState(false);

  // State for hover modal
  const [hoverModalData, setHoverModalData] = useState({
    isVisible: false,
    channel: null,
    position: { x: 0, y: 0 },
  });
  
  // Track which card is currently hovered
  const [hoveredCardIndex, setHoveredCardIndex] = useState(-1);

  // State for PDP modal
  const [pdpModalData, setPdpModalData] = useState({
    isVisible: false,
    channel: null,
    videoState: { autoPlay: false, currentTime: 0 },
  });

  // Ref for hover timeout
  const hoverTimeoutRef = useRef(null);

  // Animate results in
  useEffect(() => {
    if (hasResults && !loading) {
      const timer = setTimeout(() => setShowResults(true), 100);
      return () => clearTimeout(timer);
    } else {
      setShowResults(false);
    }
  }, [hasResults, loading]);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 7;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate range around current page
      let startPage = Math.max(2, page - 2);
      let endPage = Math.min(totalPages - 1, page + 2);

      // Adjust if we're near the beginning
      if (page <= 4) {
        endPage = Math.min(5, totalPages - 1);
      }

      // Adjust if we're near the end
      if (page >= totalPages - 3) {
        startPage = Math.max(totalPages - 4, 2);
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push('...');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page (unless it's page 1)
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle hover modal show/hide with proper timeout management
  const handleHoverModalShow = useCallback((channel, position, index) => {
    // If we're already showing a modal for a different card, hide it first
    if (hoverModalData.isVisible && hoveredCardIndex !== index) {
      // Hide current modal
      setHoverModalData(prev => ({
        ...prev,
        isVisible: false,
      }));
      
      // Reset current displacement
      resetNeighborDisplacement();
      
      // Short delay before showing the new modal to allow animation to complete
      setTimeout(() => {
        showNewModal(channel, position, index);
      }, 250); // Wait for exit animation to complete
    } else {
      // No modal showing or same card, show immediately
      showNewModal(channel, position, index);
    }
  }, [hoverModalData.isVisible, hoveredCardIndex]);
  
  // Helper function to show a new modal
  const showNewModal = useCallback((channel, position, index) => {
    // Clear any pending hide timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // Position is now relative to the results container, no scroll offset needed
    // Add a delay before showing to prevent immediate flickering and loops
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverModalData({
        isVisible: true,
        channel,
        position,
      });
      
      // Store the hovered card index and apply displacement
      setHoveredCardIndex(index);
      applyNeighborDisplacement(index);
      
    }, 500); // 500ms delay before showing (same as homepage)
  }, []);

  const handleHoverModalHide = useCallback(() => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set a timeout to hide the modal after a delay
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverModalData(prev => ({
        ...prev,
        isVisible: false,
      }));
      
      // Reset neighbor displacement and hovered index
      resetNeighborDisplacement();
      setHoveredCardIndex(-1);
      
    }, 300); // 300ms delay before hiding
  }, []);

  const handleModalMouseEnter = useCallback(() => {
    // Cancel hide timeout when mouse enters modal
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleModalMouseLeave = useCallback(() => {
    // Start hide timeout when mouse leaves modal
    handleHoverModalHide();
  }, [handleHoverModalHide]);

  const handleOpenPDPModal = useCallback(videoState => {
    // Hide the hover modal first
    setHoverModalData(prev => ({
      ...prev,
      isVisible: false,
    }));

    // Open the PDP modal with the current channel
    setPdpModalData({
      isVisible: true,
      channel: hoverModalData.channel,
      videoState: videoState || { autoPlay: false, currentTime: 0 },
    });
  }, [hoverModalData.channel]);

  // Handle direct PDP modal opening from thumbnail clicks
  const handleOpenPDPModalDirect = useCallback((channel, videoState) => {
    // Hide the hover modal first
    setHoverModalData(prev => ({
      ...prev,
      isVisible: false,
    }));

    // Open the PDP modal with the provided channel
    setPdpModalData({
      isVisible: true,
      channel: channel,
      videoState: videoState || { autoPlay: false, currentTime: 0 },
    });
  }, []);

  const handleClosePDPModal = useCallback(() => {
    setPdpModalData({
      isVisible: false,
      channel: null,
      videoState: { autoPlay: false, currentTime: 0 },
    });
  }, []);

  // Add neighbor displacement functions
  const applyNeighborDisplacement = (hoveredIndex) => {
    // Temporarily disabled - focusing on modal positioning first
    return;
  };

  const resetNeighborDisplacement = () => {
    // Temporarily disabled - focusing on modal positioning first
    return;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Render enhanced loading skeletons
  const renderSkeletons = () => {
    return Array(10)
      .fill(0)
      .map((_, index) => (
        <div
          key={`skeleton-${index}`}
          className="channel-card overflow-hidden bg-gray-900/30 rounded-lg animate-pulse"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          {/* Image skeleton with aspect ratio */}
          <div className="relative w-full aspect-video bg-gradient-to-br from-gray-800/50 to-gray-700/50">
            <div
              className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"
              style={{ animationDuration: '2s', animationDelay: `${index * 100}ms` }}
            />
          </div>

          {/* Title skeleton */}
          <div className="mt-3">
            <div className="w-3/4 h-4 rounded bg-gray-800/50" />
          </div>
        </div>
      ));
  };

  // Render horizontal suggestions
  const renderSuggestions = () => {
    // Show text suggestions if we have them and a query
    const showTextSuggestions = textSuggestions.length > 0 && query.length >= 2;

    // Show search history if no query or short query and we have history
    const showHistory = !showTextSuggestions && query.length < 2 && searchHistory.length > 0;

    if (!showTextSuggestions && !showHistory) return null;

    return (
      <div className="mb-8">
        {showTextSuggestions && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white/90">Suggestions</h3>
              {suggestionsLoading && (
                <div className="flex items-center text-sm text-white/60">
                  <div className="w-3 h-3 mr-2 border rounded-full border-white/30 border-t-teal-500 animate-spin" />
                  <span>Loading...</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              {textSuggestions.slice(0, 8).map((suggestion, index) => (
                <button
                  key={`suggestion-${index}`}
                  onClick={() => selectSuggestion(suggestion)}
                  className="px-4 py-2 transition-all duration-200 border rounded-full bg-gray-800/50 hover:bg-gray-700/50 border-white/20 hover:border-teal-500/50 text-white/80 hover:text-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {showHistory && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white/90">Recent Searches</h3>
              <button
                onClick={clearSearchHistory}
                className="text-sm text-teal-400 transition-colors duration-200 hover:text-teal-300 focus:outline-none"
              >
                Clear All
              </button>
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              {searchHistory.slice(0, 6).map((item, index) => (
                <button
                  key={`history-${index}`}
                  onClick={() => selectSuggestion(item)}
                  className="flex items-center gap-2 px-4 py-2 transition-all duration-200 border rounded-full bg-gray-800/30 hover:bg-gray-700/30 border-white/10 hover:border-teal-500/30 text-white/70 hover:text-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Separator Line */}
        <div className="mb-8 border-t border-white/10" />
      </div>
    );
  };

  // Render enhanced empty state
  const renderEmptyState = () => {
    return (
      <div className="py-16 text-center">
        <div className="mb-8">
          <svg
            className="w-24 h-24 mx-auto mb-4 text-white/20"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <h3 className="mb-2 text-2xl font-semibold text-white/80">No results found</h3>
          <p className="max-w-2xl mx-auto text-white/60">
            {query ? (
              <>
                We couldn't find any content matching "
                <span className="text-teal-400">{query}</span>". Try adjusting your search terms
                or browse our suggestions above.
              </>
            ) : (
              'Start typing to search our content library.'
            )}
          </p>
          {askArloEnabled && query && (
            <button
              type="button"
              onClick={() => openAskArlo(query)}
              className="mt-6 text-sm font-medium text-[#56bdaf] hover:text-[#4aa89c]"
            >
              Ask for a pick instead →
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render error state with built-in error handling
  const renderErrorState = () => {
    return (
      <div className="py-16 text-center">
        <div className="mb-8">
          <svg
            className="w-24 h-24 mx-auto mb-4 text-red-400/60"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>

          <h3 className="mb-2 text-2xl font-semibold text-white/80">
            {connectionError ? 'Connection Error' : 'Search Error'}
          </h3>

          <p className="max-w-2xl mx-auto mb-6 text-white/60">
            {connectionError
              ? 'Unable to connect to the server. Please check your internet connection and try again.'
              : error?.message || 'Something went wrong while searching. Please try again.'}
          </p>

          {retryCount > 0 && (
            <p className="mb-4 text-sm text-teal-400">Retry attempt: {retryCount}</p>
          )}

          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <button
              onClick={retrySearch}
              className="px-6 py-3 font-medium text-white transition-colors duration-200 bg-teal-500 rounded-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-900"
            >
              Try Again
            </button>

            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 font-medium text-white transition-colors duration-200 border rounded-lg border-white/20 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-gray-900"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="search-results">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-white md:text-4xl">
              {query ? (
                <>
                  Search results for <span className="text-teal-400">"{query}"</span>
                </>
              ) : (
                'Search Results'
              )}
            </h1>
            {totalResults > 0 && (
              <div className="flex items-center gap-3">
                <p className="text-lg text-white/70">
                  {totalResults.toLocaleString()} {totalResults === 1 ? 'result' : 'results'} found
                </p>
                {isRealTimeSearch && (
                  <div className="flex items-center gap-2 text-sm text-teal-400">
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
                    <span>Live results</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Results per page indicator */}
          {totalResults > 0 && totalPages > 1 && (
            <div className="text-sm text-white/50">
              Page {page} of {totalPages}
            </div>
          )}
        </div>
      </div>

      {/* Horizontal Suggestions */}
      {renderSuggestions()}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {renderSkeletons()}
        </div>
      ) : error ? (
        renderErrorState()
      ) : !hasResults ? (
        renderEmptyState()
      ) : (
        <>
          {/* Results Container - Positioned container for modal positioning */}
          <div className="relative">
            {/* Results Grid - Updated to match category page styling */}
            <div
              className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 transition-all duration-500 ${
                showResults ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {results.map((result, index) => (
                <div key={result._id || index} className="channel-card-container">
                  <ResultCard 
                    result={result} 
                    index={index}
                    onHoverModalShow={handleHoverModalShow}
                    onHoverModalHide={handleHoverModalHide}
                    onOpenPDPModal={handleOpenPDPModalDirect}
                  />
                </div>
              ))}
            </div>

            {/* Hover Modal - Now positioned relative to results container */}
            <ThumbnailHoverModal
              channel={hoverModalData.channel}
              isVisible={hoverModalData.isVisible}
              position={hoverModalData.position}
              onMouseEnter={handleModalMouseEnter}
              onMouseLeave={handleModalMouseLeave}
              onOpenPDPModal={handleOpenPDPModal}
            />
          </div>

          {/* Enhanced Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col items-center justify-center gap-6 mt-12 sm:flex-row">
              {/* Previous Button */}
              <button
                className={`px-6 py-3 rounded-full border transition-all duration-200 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                  isFirstPage
                    ? 'border-white/10 text-white/30 cursor-not-allowed'
                    : 'border-white/20 text-white hover:bg-white/10 hover:border-white/30'
                }`}
                onClick={() => !isFirstPage && changePage(page - 1)}
                disabled={isFirstPage}
                aria-label="Previous page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span className="hidden sm:inline">Previous</span>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-2">
                {getPageNumbers().map((pageNum, index) =>
                  pageNum === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 text-white/50">
                      ⋯
                    </span>
                  ) : (
                    <button
                      key={pageNum}
                      className={`min-w-[40px] h-10 flex items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                        pageNum === page
                          ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/25'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      onClick={() => changePage(pageNum)}
                      aria-label={`Page ${pageNum}`}
                      aria-current={pageNum === page ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  ),
                )}
              </div>

              {/* Next Button */}
              <button
                className={`px-6 py-3 rounded-full border transition-all duration-200 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                  isLastPage
                    ? 'border-white/10 text-white/30 cursor-not-allowed'
                    : 'border-white/20 text-white hover:bg-white/10 hover:border-white/30'
                }`}
                onClick={() => !isLastPage && changePage(page + 1)}
                disabled={isLastPage}
                aria-label="Next page"
              >
                <span className="hidden sm:inline">Next</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </>
      )}

      {/* PDP Modal */}
      <PDPModal
        isVisible={pdpModalData.isVisible}
        channel={pdpModalData.channel}
        onClose={handleClosePDPModal}
        videoState={pdpModalData.videoState}
      />
    </div>
  );
};

SearchResults.displayName = 'Search';

export default SearchResults;
